/**
 * API contract for the installment payoff flow (TRE-1511 / TRE-1514 / TRE-1515).
 *
 * Endpoints (products-service):
 * - POST /payoff/exchange-code { code }        -> PayoffExchangeResponse | PayoffErrorResponse
 * - GET  /payoff/quote?token=<sessionToken>    -> PayoffQuoteResponse | PayoffErrorResponse
 * - POST /payoff/execute { token, paymentMethodId? } -> PayoffExecuteResponse | PayoffErrorResponse
 * - GET  /payoff/offers (user JWT)             -> PayoffOffersResponse
 * - POST /payoff/magic-link (user JWT)         -> { url, expiresAt }
 *
 * The magic link carries a SINGLE-USE code (`/payoff/[productClientId]?code=...`).
 * The ecommerce page exchanges it server-side for a short-lived session token
 * kept in an HttpOnly cookie — the secret never reaches client JS, analytics
 * or logs. Quote and execute authorize with that session token.
 */

import { type PayoffOfferFollowUp, type PayoffOfferReward } from "./ProductClient";

/**
 * Bounds of the `bonusTime` extension (TRE-1725). Shared so the admin form, the
 * request schema and the grant use case cannot drift into disagreeing about
 * what a valid offer is. The cap is a BUSINESS limit: past a year the "reward"
 * is a free package, which is a sale someone should be making.
 */
export const MIN_BONUS_DAYS = 1;
export const MAX_BONUS_DAYS = 365;

/**
 * Payment methods the payoff page may offer (TRE-1737).
 *
 * The payoff is a ONE-OFF transaction, so it is not limited to the card saved
 * for the installment plan: a client paying her installments by transfer
 * ("raty manualne") has no saved card at all and still pays here with a card
 * or BLIK. `card` is always available; `blik` only for PLN plans on products
 * that settle BLIK through Stripe.
 */
export const PAYOFF_PAYMENT_METHODS = ["card", "blik"] as const;

export type PayoffPaymentMethod = (typeof PAYOFF_PAYMENT_METHODS)[number];

export const PayoffErrorCode = {
  /** Token/code signature invalid or malformed. */
  TokenInvalid: "token_invalid",
  /** Token/code TTL elapsed. */
  TokenExpired: "token_expired",
  /** Single-use magic-link code was already exchanged. */
  TokenUsed: "token_used",
  /** Payoff window (windowDays from purchase) has ended. */
  WindowExpired: "window_expired",
  /** Installments already fully paid (accessPeriod.status === "paid"). */
  AlreadyPaid: "already_paid",
  /** Client not eligible: no active Stripe installments, manual payment, unpaid invoice, etc. */
  NotEligible: "not_eligible",
  /** The frozen quote expired — refetch the quote and confirm the new amount. */
  QuoteExpired: "quote_expired",
  /** Off-session/on-session charge failed (card declined etc.). */
  PaymentFailed: "payment_failed",
} as const;

export type PayoffErrorCode = (typeof PayoffErrorCode)[keyof typeof PayoffErrorCode];

export interface PayoffErrorResponse {
  error: PayoffErrorCode;
  message?: string;
}

export interface PayoffQuote {
  /** Stable id of the durable payoff operation backing this quote. */
  operationId: string;
  productClientId: string;
  productId: string;
  productName: string;
  /** ISO 4217, lowercase (Stripe convention), e.g. "pln". */
  currency: string;
  /** Number of installments left to pay (excluding already invoiced ones). */
  remainingInstallments: number;
  /** Sum of the remaining installments (after active coupons), in major units. */
  remainingAmount: number;
  /**
   * Discount applied to the payoff, percentage points (e.g. 10 = 10%).
   *
   * With `rewardType: "lastInstallmentFree"` this is DERIVED from the amount
   * (savings / remainingAmount, rounded) so existing screens keep rendering —
   * the authoritative number is always `discountedAmount`.
   */
  discountPct: number;
  /**
   * How the payoff was priced (TRE-1721). Absent on quotes minted before it,
   * which the apps must read as `"discount"`.
   *
   * `bonusTime` (TRE-1725) is the one variant where `discountPct`, `savings`
   * and `discountedAmount - remainingAmount` are all zero by design — the
   * reward is `bonusDays`, not money. Screens must branch on this instead of
   * rendering "you save 0".
   */
  rewardType?: "discount" | "lastInstallmentFree" | "bonusTime";
  /** Extra access days of the `bonusTime` reward; null for every other one. */
  bonusDays?: number | null;
  /** Amount charged today, in major units. Frozen on the operation. */
  discountedAmount: number;
  /** remainingAmount - discountedAmount, in major units. */
  savings: number;
  /** Unix ms — end of the payoff window. */
  windowEndsAt: number;
  /**
   * Where the eligibility came from: the price-level `payoffUpsell` config
   * (`price`) or an offer granted to this client from the admin (`granted`,
   * TRE-1713). Drives the copy and the popup cadence in the apps.
   */
  offerSource: "price" | "granted";
  /** Unix ms the individual offer was granted; null for price-level offers. */
  grantedAt?: number | null;
  /** Unix ms — end of the frozen quote validity. */
  quoteExpiresAt: number;
  /**
   * Unix ms — entitlement end the client keeps after the payoff, INCLUDING the
   * `bonusTime` extension. It is what the page promises, so it has to be the
   * number the finalizer writes.
   */
  accessUntil: number | null;
  /** Whether the client has a reusable saved payment method (one-click path). */
  hasSavedPaymentMethod: boolean;
  /** Display data of the saved card the backend charge would use. */
  savedCard?: { brand: string; last4: string } | null;
  /**
   * Methods the page may offer for this payoff (TRE-1737). Absent on quotes
   * minted before it — read that as `["card"]`.
   */
  availablePaymentMethods?: PayoffPaymentMethod[];
  /** Connected Stripe account the charge is executed on (publishable key selection). */
  connectedStripeAccountId?: string | null;
  /** Client display/billing data. */
  client?: { email: string; firstName?: string; lastName?: string } | null;
}

export interface PayoffQuoteResponse {
  quote: PayoffQuote;
}

/** Eligible payoff offers across all of the user's active plans, best first. */
export interface PayoffOffersResponse {
  offers: PayoffQuote[];
}

/** Exchange of the single-use magic-link code for a payoff session token. */
export interface PayoffExchangeRequest {
  code: string;
}

export interface PayoffExchangeResponse {
  /** Short-lived session token scoped to one productClient (HttpOnly cookie). */
  token: string;
  /** Unix ms — session token expiry. */
  expiresAt: number;
  productClientId: string;
}

export interface PayoffExecuteRequest {
  /** Payoff session token (from the exchange), never the raw magic-link code. */
  token: string;
  /**
   * Optional new payment method created client-side via Stripe.js
   * (fallback when there is no saved PM or the saved card was declined).
   * When omitted, the backend charges the saved payment method.
   */
  paymentMethodId?: string;
  /**
   * Method the client picked (TRE-1737). `blik` never charges server-side:
   * the backend only prepares the operation's intent and answers
   * `requires_action` with its client secret, which the page confirms with
   * `stripe.confirmBlikPayment(code)`. Omitted (or `card`) keeps the original
   * behaviour — saved card off-session, or `paymentMethodId` on-session.
   */
  paymentMethod?: PayoffPaymentMethod;
}

/**
 * Admin-side grant of an individual payoff offer (TRE-1713).
 *
 * Endpoints (products-service, staff-role guarded):
 * - GET  /payoff/grant-candidates?productId=&priceId=&checkoutVariantId=
 * - POST /payoff/grant-offer   { productClientIds, reward, expiresAt, sendEmail, followUp? }
 * - POST /payoff/revoke-offer  { productClientIds }
 */
export interface PayoffOfferCandidate {
  productClientId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** ISO — purchase date of the plan. */
  createdAt: string | null;
  productId: string;
  /** Price and checkout variant of the order that established the plan. */
  priceId: string | null;
  priceDescription: string | null;
  checkoutVariantId: string | null;
  checkoutVariantSlug: string | null;
  installments: number | null;
  /** Installment cycles already billed. */
  cycleNumber: number | null;
  remainingInstallments: number | null;
  /**
   * The plan is settled by transfer, with no card on file (TRE-1737). Eligible
   * like any other, but the admin can keep such clients out of a campaign —
   * their payoff is always paid on the payoff page.
   */
  isManualPayment?: boolean;
  currentOffer: PayoffGrantedOffer | null;
  /** Cheap database-only pre-check; the grant re-validates server-side. */
  eligible: boolean;
  /** Why the client cannot be granted an offer; null when eligible. */
  reason: string | null;
}

/** Offer state as shown in the admin (mirrors ProductClientPayoffOffer). */
export interface PayoffGrantedOffer {
  enabled: boolean;
  discountPct: number;
  expiresAt: number;
  grantedAt: number;
  grantedBy: string;
  source: "admin_single" | "admin_bulk";
  batchId?: string | null;
  emailSentAt?: number | null;
  reward?: PayoffOfferReward | null;
  followUp?: PayoffOfferFollowUp | null;
  revokedAt?: number | null;
  revokedBy?: string | null;
  /** Unix ms the client actually used the offer — written by the payoff. */
  redeemedAt?: number | null;
}

export interface PayoffGrantCandidatesResponse {
  candidates: PayoffOfferCandidate[];
  /** True when the row cap was hit — narrow the filters, results are partial. */
  truncated: boolean;
}

export interface PayoffGrantOfferRequest {
  productClientIds: string[];
  /**
   * Legacy discount, still accepted so existing callers keep working. Ignored
   * when `reward` is `lastInstallmentFree`.
   */
  discountPct: number;
  /** Reward mechanic (TRE-1721); absent = the `discountPct` discount. */
  reward?: PayoffOfferReward | null;
  /** Unix ms — absolute end of the offer. */
  expiresAt: number;
  sendEmail: boolean;
  /** Reminder for clients who did not use the offer; needs `sendEmail`. */
  followUp?: { enabled: boolean; afterDays: number } | null;
}

export interface PayoffGrantOfferResponse {
  batchId: string;
  granted: string[];
  skipped: { productClientId: string; reason: string }[];
  /** Number of notification emails queued (0 when sendEmail was false). */
  emailsQueued: number;
  /** Number of reminders scheduled (0 when the follow-up was off). */
  followUpsScheduled: number;
}

/**
 * Payoff paid by transfer (TRE-1737).
 *
 * Endpoints (products-service, staff-role guarded):
 * - GET  /payoff/offline-settlement?productClientId=  -> what to suggest
 * - POST /payoff/settle-offline { productClientId, amount, note? }
 */
/**
 * Something the operator should know before booking a transfer (TRE-1745).
 * None of these blocks the settlement — they used to, which is exactly the
 * problem: a rule about who deserves an OFFER has nothing to say about money
 * that already arrived.
 */
export type PayoffSettlementWarning =
  /** An installment invoice falls within ±48h — it may already be charged. */
  | "billing_window"
  /** The plan is not `active` (typically `past_due`). */
  | "subscription_not_active"
  /** Only the last installment is left, so this is not an early payoff. */
  | "last_installment"
  /** Stripe holds an open installment invoice; the settlement voids it. */
  | "open_invoice"
  /**
   * The remaining balance could not be read from the Stripe schedule (stacked
   * coupons, a discount on a phase), so the suggested amount is the plan's
   * nominal arithmetic and can only be too high.
   */
  | "pricing_estimated";

export interface PayoffOfflineSettlementPreview {
  productClientId: string;
  currency: string;
  remainingInstallments: number;
  /** Remaining obligation in major units, before the offer's reward. */
  remainingAmount: number;
  /** What the offer would charge today, in major units — a suggestion only. */
  suggestedAmount: number;
  discountPct: number;
  rewardType: "discount" | "lastInstallmentFree" | "bonusTime";
  bonusDays: number | null;
  /** Unix ms — access horizon after the settlement, bonus days included. */
  accessUntil: number | null;
  offerSource: "price" | "granted";
  /**
   * Shown before the operator books anything; never a refusal (TRE-1745).
   *
   * OPTIONAL because it is a wire field: a products-service older than the
   * admin does not send it, and a required type only taught the compiler that
   * the guard was unnecessary — which is how the modal crashed on
   * `warnings.length` right after the admin shipped first.
   */
  warnings?: PayoffSettlementWarning[];
}

export interface PayoffSettleOfflineRequest {
  productClientId: string;
  /** Major units — what the client actually transferred. */
  amount: number;
  /** Free text kept on the operation: transfer title, date, arrangement. */
  note?: string | null;
}

export interface PayoffSettleOfflineResponse {
  orderId: string;
  /** True when the plan was already settled — a second click is not an error. */
  alreadySettled: boolean;
  /** Unix ms — access horizon the client keeps. */
  accessTo: number | null;
}

export interface PayoffRevokeOfferRequest {
  productClientIds: string[];
}

export interface PayoffRevokeOfferResponse {
  revoked: string[];
  skipped: { productClientId: string; reason: string }[];
}

export type PayoffExecuteResponse =
  /** Charge confirmed; backend finalization owns the rest. */
  | { status: "succeeded"; accessTo?: number | null }
  /**
   * SCA required — finish with Stripe.js confirmCardPayment(clientSecret) on the
   * payoff page, then POST /payoff/execute again (idempotent) to poll the result.
   */
  | { status: "requires_action"; clientSecret: string }
  /**
   * The charge outcome is not known yet (processing, or a concurrent attempt
   * holds the operation). Poll execute/quote again — never start a new card.
   */
  | { status: "processing" };
